import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { FormattedMessage as T } from "react-intl";
import { useOrganizationTheme } from "hooks/useOrganizationTheme";
import { useDeliverableContent } from "hooks/useDeliverableContent";
import PreviewHeader from "./PreviewHeader";
import ContentRenderer from "./ContentRenderer";

const useStyles = (
  tertiaryColor: string,
  primaryColor: string,
  primaryColorLight: string
) => ({
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  contentWrapper: {
    py: 2,
    backgroundColor: tertiaryColor,
  },
  contentInner: {
    maxWidth: 1200,
    mx: "auto",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    color: primaryColor,
  },
  courseActionsButton: {
    backgroundColor: primaryColorLight,
    color: "#fff",
    borderRadius: "9px",
    textTransform: "none",
    pointerEvents: "none",
    "&:hover": {
      backgroundColor: primaryColorLight,
    },
  },
  noContentMessage: {
    textAlign: "center",
    py: 4,
  },
});

type DeliveryMethodPreviewProps = {
  open: boolean;
  onClose: () => void;
  skuDeliverableId: string | undefined;
  deliveryMethodId: string | undefined;
  organizationId: string | undefined;
  displayText: string | undefined;
  width: string | undefined;
  height: string | undefined;
  skuTitle: string | undefined;
};

const DeliveryMethodPreview = ({
  open,
  onClose,
  skuDeliverableId,
  deliveryMethodId,
  organizationId,
  displayText,
  width,
  height,
  skuTitle,
}: DeliveryMethodPreviewProps) => {
  const { data: siteTheme, isFetching: isFetchingTheme } = useOrganizationTheme(
    organizationId,
    open
  );
  const tertiaryColor = siteTheme?.tertiaryColor?.base || "#f5f5f5";
  const primaryColor = siteTheme?.primaryColor?.base || "#1976d2";
  const primaryColorLight = siteTheme?.primaryColor?.light || primaryColor;
  const styles = useStyles(tertiaryColor, primaryColor, primaryColorLight);

  const {
    data: contentResponse,
    isFetching: isFetchingContent,
  } = useDeliverableContent(skuDeliverableId, open);

  const isLoading = isFetchingTheme || isFetchingContent;
  const content = contentResponse?.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <T
          id="previewDeliveryMethod"
          defaultMessage="Preview: {name}"
          values={{ name: displayText }}
        />
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <PreviewHeader siteTheme={siteTheme} />
            <Box sx={styles.contentWrapper}>
              <Box sx={styles.contentInner}>
                <Box sx={styles.titleRow}>
                  <Box sx={styles.title}>
                    <ChromeReaderModeIcon /> {skuTitle}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={
                      <MenuOpenIcon sx={{ transform: "scaleX(-1)" }} />
                    }
                    sx={styles.courseActionsButton}
                  >
                    <T id="courseActions" defaultMessage="Course Actions" />
                  </Button>
                </Box>

                <ContentRenderer
                  deliveryMethodId={deliveryMethodId}
                  content={content}
                  displayText={displayText}
                  backgroundColor={siteTheme?.backgroundColor?.base}
                  width={width}
                  height={height}
                />
              </Box>
            </Box>
            {!content && !isLoading && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={styles.noContentMessage}
              >
                <T
                  id="noContentAvailable"
                  defaultMessage="No content available for preview"
                />
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          <T id="closeButton" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryMethodPreview;
